/**
 * NewRenderCell - Renders individual cells in product rows
 *
 * Uses centralized styling from styles/utilities/view-agreement.ts
 * for consistent field heights and appearance across all field types.
 */

import DeleteIcon from '@mui/icons-material/Delete'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  useTheme,
} from '@mui/material'
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { toServiceKey } from 'components/DataGrid/operators/formatKeys'
import dayjs from 'dayjs'
import { useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useProducts } from '../../../../api/aws/useProducts'
import RichTextField from '../../../ContractDialog/MuiRichTextField'
import PaidSelect from '../../../ContractDialog/PaidSelect'
import RichTextTooltip from '../../../ContractDialog/RichTextTooltip'
import CustomProductsAutocomplete from '../../../CustomProductsAutocomplete/CustomProductsAutocomplete'
import { renderColorByString } from '../../../DataGrid/renderColorByString'
import DetailsLoading from '../../../Loading/DetailsLoading'
import { BufferedMoneyField } from '../../fields/BufferMoneyField'
import {
  productTextFieldSx,
  productSelectSx,
  productDateRangeSx,
  productAutocompleteSx,
  deleteButtonSx,
} from '../../../ProductRows/ProductTableStyles'

const productStatusOptions = [
  { label: 'Pending', color: 'gray' },
  { label: 'Active', color: 'rgb(152, 214, 129)' },
  { label: 'Monthly', color: 'rgb(1, 142, 242)' },
  { label: 'Expired', color: 'rgb(235, 77, 77)' },
  { label: 'Cancelled', color: 'rgb(0,0,0)' },
  { label: 'Ended', color: 'rgba(220, 20, 60, 0.9)' },
]

const paidProductsList = [
  'Meta Advertising',
  'Linkedin Advertising',
  'Paid Search Advertising',
  'Google Display Advertising',
  'Spotify Advertising',
  'Youtube Advertising',
  'TikTok Advertising',
]

const getCustomOverrides = (selectedProduct: any, customByServiceKey: any) => {
  const key = toServiceKey(selectedProduct)
  const custom = customByServiceKey?.get?.(key)
  if (!custom) return null
  return {
    price: custom.price,
    margin: custom.margin,
  }
}

const NewRenderCell = ({
  row,
  column,
  index,
  handleProductChange,
  validationErrors,
  handleRemoveRow,
  snapshot,
  isDraft,
  customByServiceKey,
  editableFields,
}: {
  row: any
  column: any
  index: any
  handleProductChange: any
  validationErrors: any
  handleRemoveRow: any
  snapshot?: any
  isDraft: any
  customByServiceKey?: any
  editableFields?: Set<string>
}) => {
  const theme = useTheme()

  // Field-level editability: when editableFields is provided, only those fields are editable
  const isFieldEditable = editableFields ? editableFields.has(column.id) && isDraft : isDraft
  const { data: products, isLoading: isProductsLoading } = useProducts()

  const sortedProducts = products
    ? products.sort((a, b) => {
        const categoryA = a.category_name || ''
        const categoryB = b.category_name || ''
        const categoryComparison = categoryA.localeCompare(categoryB)
        if (categoryComparison !== 0) {
          return categoryComparison
        }
        return a.title.localeCompare(b.title)
      })
    : []

  const [description, setDescription] = useState(row.description)
  const [isOpen, setIsOpen] = useState(false)

  const handleSaveDescription = () => {
    handleProductChange(index, 'description', description)
    handleClose()
  }

  const handleProductSelect = (idx: number, selectedProduct: any) => {
    if (selectedProduct === null) {
      handleProductChange(idx, 'selectedProduct', null, {
        description: '',
        margin: '0',
        productName: '',
        price: '0.00',
      })
      return
    }

    const prevKey = toServiceKey(row?.selectedProduct || row?.productName || '')
    const nextKey = toServiceKey(selectedProduct)
    const selectionChanged = prevKey !== nextKey

    const baseUpdates = {
      instanceId: uuidv4(),
      description: selectedProduct.description,
      productName: selectedProduct.title,
      category: selectedProduct.category_name || '',
      subcategory: selectedProduct.subcategory || '',
    }

    const catalogDefaults = selectionChanged
      ? {
          margin:
            selectedProduct.custom_fields?.find((f: any) => f.name === 'Margin %')?.value ||
            selectedProduct.margin ||
            '0',
          price: selectedProduct.price || '0.00',
        }
      : {}

    const customOverrides = selectionChanged ? getCustomOverrides(selectedProduct, customByServiceKey) : null

    const updates: Record<string, any> = {
      ...baseUpdates,
      ...catalogDefaults,
      ...(customOverrides ? { margin: String(customOverrides.margin), price: String(customOverrides.price) } : {}),
    }

    if (paidProductsList.includes(selectedProduct.title)) {
      updates.payer = 'GRO'
      updates.accountUsed = 'GRO'
    }

    handleProductChange(idx, 'selectedProduct', selectedProduct, updates)
  }

  const handleOpen = () => {
    setDescription(row.description)
    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const cellValue = row[column.id]
  const hasError = validationErrors.products && !row.productName
  const paidSelectLabels = ['Payer', 'Account Used']
  const isProductPaid = paidProductsList.includes(row.productName)
  const displayLabel = column && column.id === 'payer' ? 'Payer' : 'Account Used'
  const isDisabled = column && column.id === 'payer' ? !isProductPaid : !isProductPaid
  const defaultValue = 'GRO'

  // Get consistent field styles from centralized module
  const textFieldStyles = productTextFieldSx(theme)
  const selectStyles = productSelectSx(theme)
  const dateRangeStyles = productDateRangeSx(theme)
  const autocompleteStyles = productAutocompleteSx(theme)
  const deleteStyles = deleteButtonSx()

  if (isProductsLoading) {
    return (
      <Box sx={{ width: '100%', height: 40, display: 'flex', alignItems: 'center' }}>
        <DetailsLoading />
      </Box>
    )
  }

  // Money fields (Price, Margin, Total, etc.) - use short labels to save space
  const moneyLabels = ['Total', 'Gross Profit', 'Media Spend', 'Margin', 'Price', 'Billed Price', 'Net Total']
  const shortLabels: Record<string, string> = {
    'Media Spend': 'Spend',
    'Gross Profit': 'Profit',
    'Billed Price': 'Billed',
    'Net Total': 'Net',
  }
  if (moneyLabels.includes(column.label)) {
    const displayLabel = shortLabels[column.label] || column.label
    return (
      <BufferedMoneyField
        label={displayLabel}
        value={cellValue ?? ''}
        disabled={!isFieldEditable}
        error={validationErrors?.[column.id]}
        onCommit={next => handleProductChange(index, column.id, next)}
      />
    )
  }

  // Quantity field
  if (column.label === 'Qty') {
    return (
      <TextField
        type="text"
        fullWidth
        label={column.label}
        variant="outlined"
        size="small"
        value={cellValue === '' ? '' : parseFloat(cellValue)}
        onChange={e => handleProductChange(index, column.id, e.target.value)}
        onBlur={e => {
          if (e.target.value === '') {
            handleProductChange(index, column.id, 0.0)
          }
        }}
        disabled={!isFieldEditable}
        sx={textFieldStyles}
        error={validationErrors.price}
      />
    )
  }

  // Status select
  if (column.label === 'Status') {
    return (
      <TextField
        select
        fullWidth
        label="Status"
        variant="outlined"
        size="small"
        value={cellValue || ''}
        onChange={e => handleProductChange(index, column.id, e.target.value)}
        disabled={!isFieldEditable}
        sx={{
          ...selectStyles,
          '& .MuiSelect-select': {
            color: renderColorByString(cellValue),
          },
        }}
      >
        {productStatusOptions.map(option => (
          <MenuItem key={option.label} value={option.label} style={{ color: renderColorByString(option.label) }}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  // Date Range picker
  if (column?.label === 'Date Range') {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs as any}>
        <DateRangePicker
          value={
            (Array.isArray(cellValue) && cellValue.length === 2
              ? cellValue.map(date => (dayjs.isDayjs(date) ? date : dayjs(date).isValid() ? dayjs(date) : null))
              : [null, null]) as [any, any]
          }
          onChange={newValue => {
            const sanitizedValue = Array.isArray(newValue)
              ? newValue.map(date => (dayjs.isDayjs(date) ? date.toISOString() : null))
              : [null, null]
            handleProductChange(index, 'dateRange', sanitizedValue)
          }}
          disabled={!isFieldEditable}
          sx={dateRangeStyles}
        />
      </LocalizationProvider>
    )
  }

  // Paid Select (Payer, Account Used)
  if (paidSelectLabels?.includes(column?.label)) {
    return (
      <PaidSelect
        context={column.id}
        displayLabel={displayLabel}
        value={isDisabled ? defaultValue : cellValue || ''}
        setterFn={(newValue: string) => handleProductChange(index, column.id, newValue)}
        disabled={isDisabled || !isFieldEditable}
        isDraft={isFieldEditable}
      />
    )
  }

  // Description popover
  if (column?.type === 'popover') {
    return (
      <>
        <RichTextTooltip content={row.description || 'No description'} onClick={handleOpen} isDraft={isFieldEditable} />
        {isOpen && (
          <Dialog
            open={isOpen}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            disableEnforceFocus
            disableAutoFocus
            disableRestoreFocus
          >
            <DialogTitle>Edit Description</DialogTitle>
            <DialogContent>
              <Box sx={{ width: '100%', margin: 0 }}>
                <RichTextField content={description || '<p></p>'} onContentChange={setDescription} onBlur={() => {}} />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <Button onClick={handleSaveDescription} color="primary">
                Done
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </>
    )
  }

  // Product Autocomplete
  if (column?.type === 'autocomplete') {
    return (
      <CustomProductsAutocomplete
        products={sortedProducts}
        value={row.selectedProduct ? row.selectedProduct : products.find((product: any) => product.title === row.productName)}
        onChange={newValue => handleProductSelect(index, newValue)}
        label="Select Service"
        error={hasError}
        isLoading={isProductsLoading}
        disabled={!isFieldEditable}
        sx={autocompleteStyles}
      />
    )
  }

  // Delete button — enabled when draft OR when editableFields allows product changes
  if (column?.type === 'component') {
    const canRemove = editableFields ? editableFields.has('productName') && isDraft : isDraft
    return (
      <Box sx={deleteStyles}>
        <IconButton onClick={() => handleRemoveRow(row.SK)} color="error" disabled={!canRemove} size="small">
          <DeleteIcon
            sx={{
              fontSize: 20,
              color: !canRemove ? theme.palette.text.disabled : theme.palette.error.main,
            }}
          />
        </IconButton>
      </Box>
    )
  }

  // Additional descriptor field
  if (column.id === 'addtlDescriptor') {
    return (
      <TextField
        type="text"
        fullWidth
        placeholder="additional descriptor"
        variant="outlined"
        size="small"
        value={cellValue || ''}
        onChange={e => handleProductChange(index, column.id, e.target.value)}
        disabled={!isFieldEditable}
        sx={textFieldStyles}
      />
    )
  }

  return null
}

export default NewRenderCell
